import React, { useState, useEffect } from 'react';
import { Edit, Save, Plus, Trash2, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { collection, doc, getDoc, setDoc, deleteDoc, getDocs, query, where } from 'firebase/firestore';
import { firestore } from '../../lib/firebase/config';
import { useLanguage } from '../../contexts/LanguageContext';
import { RiyalIcon } from './icons';
import { useAmountVisibility, maskAmount } from './AmountVisibilityContext';

type ConfigRow = {
  id: string;
  organizerId: string;
  organizerName: string;
  baseAmount: number;
  bonusThreshold: number;
  bonusAmount: number;
  notOwnerAmount: number;
};

type Props = {
  organizers: Record<string, any>;
  onConfigChange: () => void;
};

export const MoneyConfigTable = ({ organizers, onConfigChange }: Props) => {
  const { translations } = useLanguage();
  const [configs, setConfigs] = useState<ConfigRow[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [newConfig, setNewConfig] = useState<Partial<ConfigRow>>({
    baseAmount: 0,
    bonusThreshold: 500,
    bonusAmount: 0,
    notOwnerAmount: 0
  });
  
  const [editValues, setEditValues] = useState<Partial<ConfigRow>>({});
  const { showAmounts } = useAmountVisibility();

  const loadConfigs = async () => {
    setIsLoading(true);
    try {
      const configsRef = collection(firestore, 'FinanceConfig');
      const configSnapshot = await getDocs(configsRef);
      
      const loadedConfigs: ConfigRow[] = [];
      let hasDefault = false;
      
      configSnapshot.forEach(doc => {
        const data = doc.data() as ConfigRow;
        
        if (data.organizerId === 'notOwner') {
          return;
        }
        
        if (data.notOwnerAmount === undefined) {
          data.notOwnerAmount = 0;
        }
        
        loadedConfigs.push({
          ...data,
          id: doc.id
        });
        
        if (data.organizerId === 'default') {
          hasDefault = true;
        }
      });
      
      if (!hasDefault) {
        const defaultConfig: ConfigRow = {
          id: 'default',
          organizerId: 'default',
          organizerName: 'Default',
          baseAmount: 2000,
          bonusThreshold: 500,
          bonusAmount: 500,
          notOwnerAmount: 0
        };
        
        await setDoc(doc(firestore, 'FinanceConfig', 'default'), defaultConfig);
        loadedConfigs.push(defaultConfig);
      }
      
      const sortedConfigs = loadedConfigs.sort((a, b) => {
        if (a.organizerId === 'default') return -1;
        if (b.organizerId === 'default') return 1;
        return a.organizerName.localeCompare(b.organizerName);
      });
      
      setConfigs(sortedConfigs);
    } catch (error) {
      console.error('Error loading finance configs:', error);
      toast.error(translations?.organizerFinanceconfigure?.errors?.loadConfigs || 'Failed to load finance configuration');
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    loadConfigs();
  }, []);
  
  const handleSave = async (id: string) => {
    try {
      const configRef = doc(firestore, 'FinanceConfig', id);
      await setDoc(configRef, {
        ...configs.find(c => c.id === id),
        ...editValues,
        baseAmount: Number(editValues.baseAmount),
        bonusThreshold: Number(editValues.bonusThreshold),
        bonusAmount: Number(editValues.bonusAmount),
        notOwnerAmount: Number(editValues.notOwnerAmount)
      }, { merge: true });
      
      setConfigs(configs.map(config => 
        config.id === id ? { 
          ...config, 
          ...editValues,
          baseAmount: Number(editValues.baseAmount),
          bonusThreshold: Number(editValues.bonusThreshold),
          bonusAmount: Number(editValues.bonusAmount),
          notOwnerAmount: Number(editValues.notOwnerAmount)
        } : config
      ));
      
      setEditingId(null);
      setEditValues({});
      toast.success(translations?.organizerFinanceconfigure?.success?.saveConfig || 'Configuration saved successfully');
      onConfigChange();
    } catch (error) {
      console.error('Error updating config:', error);
      toast.error(translations?.organizerFinanceconfigure?.errors?.saveConfig || 'Failed to save configuration');
    }
  };
  
  const handleDelete = async (id: string) => {
    if (id === 'default') {
      toast.error(translations?.organizerFinanceconfigure?.errors?.deleteDefault || 'Cannot delete default configuration');
      return;
    }
    
    try {
      await deleteDoc(doc(firestore, 'FinanceConfig', id));
      setConfigs(configs.filter(config => config.id !== id));
      toast.success(translations?.organizerFinanceconfigure?.success?.deleteConfig || 'Configuration deleted successfully');
      onConfigChange();
    } catch (error) {
      console.error('Error deleting config:', error);
      toast.error(translations?.organizerFinanceconfigure?.errors?.deleteConfig || 'Failed to delete configuration');
    }
  };
  
  const handleAdd = async () => {
    if (!newConfig.organizerId) {
      toast.error(translations?.organizerFinanceconfigure?.errors?.selectOrganizer || 'Please select an organizer');
      return;
    }
    
    const existing = configs.find(c => c.organizerId === newConfig.organizerId);
    if (existing) {
      toast.error(translations?.organizerFinanceconfigure?.errors?.configExists || 'Configuration already exists for this organizer');
      return;
    }
    
    try {
      const configRef = doc(collection(firestore, 'FinanceConfig'));
      const selectedOrganizer = organizers[newConfig.organizerId as string];
      
      const configData: ConfigRow = {
        id: configRef.id,
        organizerId: newConfig.organizerId as string,
        organizerName: selectedOrganizer.name,
        baseAmount: Number(newConfig.baseAmount) || 0,
        bonusThreshold: Number(newConfig.bonusThreshold) || 500,
        bonusAmount: Number(newConfig.bonusAmount) || 0,
        notOwnerAmount: Number(newConfig.notOwnerAmount) || 0
      };
      
      await setDoc(configRef, configData);
      
      setConfigs([...configs, configData]);
      setIsAdding(false);
      setNewConfig({
        baseAmount: 0,
        bonusThreshold: 500,
        bonusAmount: 0,
        notOwnerAmount: 0
      });
      
      toast.success(translations?.organizerFinanceconfigure?.success?.addConfig || 'New configuration added successfully');
      onConfigChange();
    } catch (error) {
      console.error('Error adding config:', error);
      toast.error(translations?.organizerFinanceconfigure?.errors?.addConfig || 'Failed to add configuration');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white p-6 rounded-xl shadow-sm"
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold">
          {translations?.organizerFinanceconfigure?.configTable?.title || 'Finance Configuration'}
        </h2>
        <div className="flex gap-3">
          <button
            onClick={() => setIsAdding(!isAdding)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors"
          >
            {isAdding ? (
              <><RefreshCw size={18} /> {translations?.commonconfigur?.actions?.cancel || 'Cancel'}</>
            ) : (
              <><Plus size={18} /> {translations?.organizerFinanceconfigure?.actions?.addConfig || 'Add Custom Configuration'}</>
            )}
          </button>
          <button
            onClick={loadConfigs}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <RefreshCw size={18} />
            <span>{translations?.commonconfigur?.actions?.refresh || 'Refresh'}</span>
          </button>
        </div>
      </div>

      {isAdding && (
        <div className="mb-6 p-4 bg-primary-50 rounded-lg">
          <h3 className="font-medium mb-3">
            {translations?.organizerFinanceconfigure?.configTable?.addTitle || 'Add New Configuration'}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {translations?.organizerFinanceconfigure?.labels?.organizer || 'Organizer'}
              </label>
              <select
                value={newConfig.organizerId || ''}
                onChange={(e) => setNewConfig({ ...newConfig, organizerId: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="">{translations?.organizerFinanceconfigure?.placeholders?.selectOrganizer || 'Select Organizer'}</option>
                {Object.entries(organizers)
                  .filter(([id]) => !configs.some(c => c.organizerId === id))
                  .map(([id, organizer]) => (
                    <option key={id} value={id}>{organizer.name}</option>
                  ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {translations?.organizerFinanceconfigure?.labels?.baseAmount || 'Base Amount'}
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={newConfig.baseAmount || 0}
                  onChange={(e) => setNewConfig({ ...newConfig, baseAmount: Number(e.target.value) })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <RiyalIcon size={16} className="text-gray-400" />
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {translations?.organizerFinanceconfigure?.labels?.bonusAmount || 'Bonus Amount'}
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={newConfig.bonusAmount || 0}
                  onChange={(e) => setNewConfig({ ...newConfig, bonusAmount: Number(e.target.value) })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <RiyalIcon size={16} className="text-gray-400" />
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {translations?.organizerFinanceconfigure?.labels?.notOwnerAmount || 'Not Owner Amount'}
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={newConfig.notOwnerAmount || 0}
                  onChange={(e) => setNewConfig({ ...newConfig, notOwnerAmount: Number(e.target.value) })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <RiyalIcon size={16} className="text-gray-400" />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={handleAdd}
              className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              <Save size={18} />
              <span>{translations?.commonconfigur?.actions?.save || 'Save'}</span>
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="py-8 text-center">
          <div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-2 text-gray-500">
            {translations?.organizerFinanceconfigure?.loading?.configs || 'Loading configurations...'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {translations?.organizerFinanceconfigure?.tableHeaders?.organizer || 'Organizer'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {translations?.organizerFinanceconfigure?.tableHeaders?.baseAmount || 'Base Amount'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {translations?.organizerFinanceconfigure?.tableHeaders?.bonusAmount || 'Bonus Amount'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {translations?.organizerFinanceconfigure?.tableHeaders?.notOwnerAmount || 'Not Owner Amount'}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {translations?.organizerFinanceconfigure?.tableHeaders?.actions || 'Actions'}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {configs.map((config) => (
                <tr key={config.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-sm font-medium text-gray-900">
                        {config.organizerId === 'default' ? (
                          <span className="font-semibold text-primary-600">
                            {translations?.organizerFinanceconfigure?.labels?.default || 'Default'}
                          </span>
                        ) : (
                          config.organizerName
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingId === config.id ? (
                      <div className="relative">
                        <input
                          type="number"
                          value={editValues.baseAmount !== undefined ? editValues.baseAmount : config.baseAmount}
                          onChange={(e) => setEditValues({ ...editValues, baseAmount: Number(e.target.value) })}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <RiyalIcon size={16} className="text-gray-400" />
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <span className="text-sm text-gray-900">{maskAmount(config.baseAmount, showAmounts)}</span>
                        <RiyalIcon size={16} className="ml-1 text-gray-400" />
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingId === config.id ? (
                      <div className="relative">
                        <input
                          type="number"
                          value={editValues.bonusAmount !== undefined ? editValues.bonusAmount : config.bonusAmount}
                          onChange={(e) => setEditValues({ ...editValues, bonusAmount: Number(e.target.value) })}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <RiyalIcon size={16} className="text-gray-400" />
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <span className="text-sm text-gray-900">{maskAmount(config.bonusAmount, showAmounts)}</span>
                        <RiyalIcon size={16} className="ml-1 text-gray-400" />
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingId === config.id ? (
                      <div className="relative">
                        <input
                          type="number"
                          value={editValues.notOwnerAmount !== undefined ? editValues.notOwnerAmount : config.notOwnerAmount}
                          onChange={(e) => setEditValues({ ...editValues, notOwnerAmount: Number(e.target.value) })}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <RiyalIcon size={16} className="text-gray-400" />
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <span className="text-sm text-gray-900">{maskAmount(config.notOwnerAmount, showAmounts)}</span>
                        <RiyalIcon size={16} className="ml-1 text-gray-400" />
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {editingId === config.id ? (
                      <button
                        onClick={() => handleSave(config.id)}
                        className="text-green-600 hover:text-green-800 mr-3"
                      >
                        <Save size={18} />
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          setEditingId(config.id);
                          setEditValues({
                            baseAmount: config.baseAmount,
                            bonusThreshold: config.bonusThreshold,
                            bonusAmount: config.bonusAmount,
                            notOwnerAmount: config.notOwnerAmount
                          });
                        }}
                        className="text-blue-600 hover:text-blue-800 mr-3"
                        disabled={isLoading}
                      >
                        <Edit size={18} />
                      </button>
                    )}
                    
                    {config.organizerId !== 'default' && (
                      <button
                        onClick={() => handleDelete(config.id)}
                        className="text-red-600 hover:text-red-800"
                        disabled={isLoading}
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  );
};